import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { news } from '@/mocks/news';
import usePageMeta from '@/hooks/usePageMeta';
import { formatDate } from '@/utils/format';

const CATEGORIES = ['全部', '观影指南', '幕后制作', '行业观察', '影视评论'];
const PAGE_SIZE = 4;

export default function NewsPage() {
  const [category, setCategory] = useState('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta(
    '影视资讯与观影指南 - 快活影院',
    '快活影院资讯专题，提供最新影视新闻、幕后制作揭秘、观影指南与行业观察，帮助你更懂影视内容。',
    '影视资讯,观影指南,幕后制作,行业观察,快活影院',
  );

  const filtered = useMemo(
    () => (category === '全部' ? news : news.filter((item) => item.category === category)),
    [category],
  );

  const list = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const changeCategory = (next: string) => {
    setCategory(next);
    setVisible(PAGE_SIZE);
  };

  const featured = news[0];

  return (
    <div className="w-full pb-4">
      {/* hero */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={featured.cover}
          alt="资讯专题头图"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/40"></div>
        <div className="relative max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="text-sm text-background-200/80">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-accent-400 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">资讯</li>
            </ol>
          </nav>
          <h1 className="mt-5 font-heading font-extrabold text-3xl md:text-4xl text-background-50">
            影视资讯专题
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-background-100/90">
            从幕后制作到行业观察，从观影指南到影视评论——我们把值得一读的影视内容整理在这里，
            帮你在按下播放键之前，先多看一眼这个世界。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* category filter */}
        <div className="mt-8 flex items-center gap-1.5 overflow-x-auto kh-scrollbar-hide">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => changeCategory(item)}
              className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                category === item
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {/* list */}
        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-10">
            <div className="space-y-5">
              {list.map((article) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <span className="w-full sm:w-52 h-40 sm:h-32 rounded-md overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={article.cover}
                      alt={`${article.title}配图`}
                      title={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-900 text-xs">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{formatDate(article.date)}</span>
                      <span className="text-xs text-foreground-400">· {article.author}</span>
                    </span>
                    <span className="block mt-2 font-heading font-bold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </span>
                    <span className="block mt-2 text-sm leading-6 text-foreground-500 line-clamp-2">
                      {article.summary}
                    </span>
                  </span>
                </Link>
              ))}

              <div className="pt-2 text-center">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-down-line"></i>
                    加载更多资讯
                  </button>
                ) : (
                  <p className="text-sm text-foreground-400">— 已经到底啦 —</p>
                )}
              </div>
            </div>

            {/* sidebar */}
            <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
              <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500"></i>
                  编辑推荐阅读
                </h2>
                <ol className="space-y-3">
                  {news.slice(0, 5).map((article, i) => (
                    <li key={article.id}>
                      <Link to={`/news/${article.id}`} className="flex gap-3 group">
                        <span className={`font-heading font-extrabold text-sm shrink-0 ${i < 3 ? 'text-accent-500' : 'text-foreground-300'}`}>
                          {i + 1}
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-links-line text-secondary-500"></i>
                  相关专题入口
                </h2>
                <div className="space-y-2">
                  <Link to="/category/movie" className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                    电影频道
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                  <Link to="/category/tv" className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                    电视剧频道
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                  <Link to="/about" className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                    关于快活影院
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        ) : (
          <div className="mt-10 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-article-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
              该分类下暂无资讯内容
            </h2>
            <button
              type="button"
              onClick={() => changeCategory('全部')}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              查看全部资讯
            </button>
          </div>
        )}
      </div>
    </div>
  );
}